import { useRef, useState } from 'react';
import type { StepRoi, RoiKind } from '@visionqc/shared';

/**
 * Draw regions of interest for a step on a captured camera frame. The same
 * click-to-add / click-first-to-close interaction as the safety ZoneEditor, but
 * bound to a step's `rois` array rather than the zones API. Coordinates are
 * normalised [0,1] so an ROI survives any display size. Falls back to a neutral
 * grid when the frame can't be grabbed (e.g. camera offline), so authoring never
 * blocks on a live feed.
 */

const KIND_COLOUR: Record<RoiKind, string> = {
  action: 'var(--accent)',
  component: 'var(--teal)',
  inspect: 'var(--violet)',
};

export function RoiCanvas({
  imageUrl,
  cameraId,
  rois,
  onChange,
}: {
  imageUrl: string | null;
  cameraId: number | null;
  rois: StepRoi[];
  onChange: (rois: StepRoi[]) => void;
}) {
  const boxRef = useRef<HTMLDivElement>(null);
  const [drawing, setDrawing] = useState<Array<{ x: number; y: number }>>([]);
  const [imgOk, setImgOk] = useState(true);

  // ROIs drawn on the camera currently shown (null cameraId = the primary).
  const mine = rois.filter((r) => (r.cameraId ?? null) === (cameraId ?? null));

  const toNorm = (clientX: number, clientY: number) => {
    const rect = boxRef.current!.getBoundingClientRect();
    return {
      x: Math.min(1, Math.max(0, (clientX - rect.left) / rect.width)),
      y: Math.min(1, Math.max(0, (clientY - rect.top) / rect.height)),
    };
  };

  function click(e: React.MouseEvent) {
    const p = toNorm(e.clientX, e.clientY);
    if (drawing.length >= 3) {
      const first = drawing[0]!;
      if (Math.hypot(p.x - first.x, p.y - first.y) < 0.03) return finish();
    }
    setDrawing((d) => [...d, p]);
  }

  function finish() {
    if (drawing.length >= 3) {
      onChange([...rois, { name: '', kind: 'action', cameraId, polygon: drawing }]);
    }
    setDrawing([]);
  }

  const removeRoi = (roi: StepRoi) => onChange(rois.filter((r) => r !== roi));
  const setKind = (roi: StepRoi, kind: RoiKind) => onChange(rois.map((r) => (r === roi ? { ...r, kind } : r)));

  return (
    <div className="grid" style={{ gridTemplateColumns: 'minmax(0,1fr) 200px', gap: 12 }}>
      <div
        ref={boxRef}
        onClick={click}
        style={{
          position: 'relative',
          aspectRatio: '4 / 3',
          borderRadius: 8,
          overflow: 'hidden',
          cursor: 'crosshair',
          background: imgOk && imageUrl ? '#000' : 'var(--bg-panel-2)',
          backgroundImage: imgOk
            ? undefined
            : 'linear-gradient(var(--border) 1px, transparent 1px), linear-gradient(90deg, var(--border) 1px, transparent 1px)',
          backgroundSize: '28px 28px',
        }}
      >
        {imgOk && imageUrl && (
          <img
            src={imageUrl}
            alt="camera frame"
            onError={() => setImgOk(false)}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', pointerEvents: 'none' }}
          />
        )}
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
          {mine.map((r, i) => (
            <polygon
              key={i}
              points={r.polygon.map((p) => `${p.x * 100},${p.y * 100}`).join(' ')}
              fill={`color-mix(in srgb, ${KIND_COLOUR[r.kind]} 22%, transparent)`}
              stroke={KIND_COLOUR[r.kind]}
              strokeWidth={0.6}
              vectorEffect="non-scaling-stroke"
            />
          ))}
          {drawing.length > 0 && (
            <polyline
              points={drawing.map((p) => `${p.x * 100},${p.y * 100}`).join(' ')}
              fill="none"
              stroke="var(--warn)"
              strokeWidth={0.6}
              vectorEffect="non-scaling-stroke"
            />
          )}
          {drawing.map((p, i) => (
            <circle key={i} cx={p.x * 100} cy={p.y * 100} r={0.9} fill="var(--warn)" />
          ))}
        </svg>
        {!imgOk && (
          <div className="muted" style={{ position: 'absolute', bottom: 6, left: 8, fontSize: 11 }}>
            No live frame — drawing on a grid
          </div>
        )}
      </div>

      <div>
        <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
          Click to add points; click the first point (or double-click) to close.
        </div>
        {drawing.length > 0 && (
          <div className="row" style={{ gap: 6, marginBottom: 10 }}>
            <button className="btn sm primary" onClick={finish} disabled={drawing.length < 3}>Finish ROI</button>
            <button className="btn sm" onClick={() => setDrawing([])}>Cancel</button>
          </div>
        )}
        {mine.length === 0 && drawing.length === 0 && (
          <div className="muted" style={{ fontSize: 12 }}>No ROI yet for this camera.</div>
        )}
        {mine.map((r, i) => (
          <div key={i} className="panel" style={{ padding: 8, marginBottom: 8, borderLeft: `3px solid ${KIND_COLOUR[r.kind]}` }}>
            <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
              <select value={r.kind} onChange={(e) => setKind(r, e.target.value as RoiKind)} style={{ fontSize: 12 }}>
                <option value="action">Action</option>
                <option value="component">Component</option>
                <option value="inspect">Inspect</option>
              </select>
              <button className="btn sm bad" onClick={() => removeRoi(r)}>Remove</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
